import { Button, Modal, Icon, Typography, IconVariant } from "@/components/atoms";
import { clsx, openUrlWithGoogleDriveSupport } from "@/utils";

export interface QuickSetupModalAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "tertiary";
  disabled?: boolean;
}

export interface WhyExplanation {
  type: "success" | "error" | "warning" | "info";
  title: string;
  description: string;
}

export type QuickSetupModalContentType =
  | { type: "custom"; content: React.ReactNode }
  | { type: "image"; imageUrl: string; enableDownload?: boolean; downloadImageUrl?: string }
  | { type: "video"; videoUrl: string }
  | { type: "documentation"; content: React.ReactNode | string; pdfUrl?: string }
  | { type: "setup-guide"; documentationUrl: string; pdfUrl?: string }
  | { type: "why"; explanations: WhyExplanation[] };

export interface QuickSetupModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  content: QuickSetupModalContentType;
  actions?: QuickSetupModalAction[];
  variant?: "web-small" | "web-medium" | "web-large" | "mobile";
  className?: string;
  featuredIcon?: {
    name: string;
    background?: string;
    className?: string;
    isCustom?: boolean;
    variant?: IconVariant;
  };
}

export const QuickSetupModal = ({
  open,
  onClose,
  title,
  subtitle,
  content,
  actions = [],
  variant = "web-large",
  className,
  featuredIcon,
}: QuickSetupModalProps) => {
  const handleImageDownload = (imageUrl: string) => {
    openUrlWithGoogleDriveSupport(imageUrl);
  };

  const handlePdfOpen = (pdfUrl: string) => {
    openUrlWithGoogleDriveSupport(pdfUrl);
  };

  const getProcessedActions = (): QuickSetupModalAction[] => {
    const baseActions = [...actions];
    const closeAction: QuickSetupModalAction = {
      label: "Close",
      onClick: onClose,
      variant: "secondary",
    };

    if (content.type === "image" && content.enableDownload) {
      baseActions.push(closeAction, {
        label: "Download Image",
        onClick: () => handleImageDownload(content.downloadImageUrl ?? content.imageUrl),
        variant: "primary",
      });
    }

    if (content.type === "documentation" && content.pdfUrl) {
      baseActions.push(closeAction, {
        label: "Download PDF",
        onClick: () => handlePdfOpen(content.pdfUrl!),
        variant: "primary",
      });
    }

    if (content.type === "setup-guide" && content.pdfUrl) {
      baseActions.push(closeAction, {
        label: "Download PDF",
        onClick: () => handlePdfOpen(content.pdfUrl!),
        variant: "primary",
      });
    }

    if (baseActions.length === 0) {
      baseActions.push(closeAction);
    }

    return baseActions;
  };

  const renderFeaturedIcon = () => {
    if (!featuredIcon) return undefined;

    return (
      <div className={clsx("flex h-8 w-8 items-center justify-center rounded", featuredIcon.background || "bg-primary-light")}>
        <Icon
          name={featuredIcon.name}
          size="md"
          className={`text-primary-dark ${featuredIcon.className || ""}`}
          isCustom={featuredIcon.isCustom}
          variant={featuredIcon.variant}
        />
      </div>
    );
  };

  const renderContent = () => {
    switch (content.type) {
      case "image":
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-4xl">
              <img
                src={content.imageUrl}
                alt={title}
                className="h-auto w-full rounded-lg border border-neutral-mid-gray object-contain"
                style={{ maxHeight: "60vh" }}
              />
            </div>
          </div>
        );

      case "video":
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-4xl">
              <iframe
                src={`${content.videoUrl}${content.videoUrl.includes("?") ? "&" : "?"}as=video`}
                allow="fullscreen"
                className="h-[50vh] max-h-[800px] min-h-[350px] w-full rounded-lg border border-neutral-mid-gray md:h-[60vh]"
                title="Walkthrough Video"
              />
            </div>
          </div>
        );

      case "documentation":
        return (
          <>
            <style>{`
              .documentation-scrollable::-webkit-scrollbar {
                width: 6px;
              }
              .documentation-scrollable::-webkit-scrollbar-track {
                background: transparent;
              }
              .documentation-scrollable::-webkit-scrollbar-thumb {
                background-color: #98a2b3;
                border-radius: 100px;
                min-height: 30px;
              }
              .documentation-scrollable {
                scrollbar-width: thin;
                scrollbar-color: #98a2b3 transparent;
              }
            `}</style>
            <div className="relative overflow-hidden rounded-lg bg-neutral-surface-gray">
              <div className="documentation-scrollable h-[300px] overflow-y-auto pr-3 md:h-[400px]">
                <div className="p-2.5">
                  {typeof content.content === "string" ? (
                    <div className="space-y-4">
                      {content.content.split("\n\n").map((paragraph, pIndex) => (
                        <Typography key={pIndex} variant="paragraph" className="text-neutral-dark-gray">
                          {paragraph}
                        </Typography>
                      ))}
                    </div>
                  ) : (
                    content.content
                  )}
                </div>
              </div>
            </div>
          </>
        );

      case "why":
        return (
          <div className="space-y-3">
            {content.explanations.map((explanation, index) => {
              const getIconAndColor = () => {
                switch (explanation.type) {
                  case "success":
                    return { icon: "TickCircle", color: "text-success" };
                  case "error":
                    return { icon: "CloseCircle", color: "text-danger" };
                  case "warning":
                    return { icon: "InfoCircle", color: "text-warning" };
                  case "info":
                  default:
                    return { icon: "InfoCircle", color: "text-info" };
                }
              };

              const { icon, color } = getIconAndColor();

              return (
                <div key={index} className="flex flex-col gap-1 rounded-lg bg-neutral-surface-gray p-2.5">
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                      <Icon name={icon} size="sm" className={color} variant="Bold" />
                    </div>
                    <Typography variant="action">{explanation.title}</Typography>
                  </div>

                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    {explanation.description}
                  </Typography>
                </div>
              );
            })}
          </div>
        );

      case "setup-guide":
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-4xl">
              <iframe
                src={content.documentationUrl}
                width="100%"
                allow="fullscreen"
                className="h-[50vh] max-h-[800px] min-h-[350px] w-full rounded-lg border border-neutral-mid-gray md:h-[60vh]"
                title="Setup Guide"
              />
            </div>
          </div>
        );

      case "custom":
      default:
        return content.type === "custom" ? content.content : null;
    }
  };

  const processedActions = getProcessedActions();

  const renderButtons =
    processedActions.length > 0
      ? () => (
          <>
            {processedActions.map((action, index) => (
              <Button key={index} variant={action.variant || "secondary"} onClick={action.onClick} disabled={action.disabled}>
                {action.label}
              </Button>
            ))}
          </>
        )
      : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={subtitle}
      renderButtons={renderButtons}
      variant={variant}
      className={clsx("overflow-y-auto", className)}
      featuredIcon={renderFeaturedIcon()}
    >
      {renderContent()}
    </Modal>
  );
};
